import { parseLanguageCode } from "../../../i18n/utils";
import { RichText } from "../../cms/RichText/RichText";
import { locationSelectors } from "../../store/slice/locationSlice";
import { IS_PROD } from "../../utils/currentEnv";
import Heading from "../ui/Heading";
import Tag from "../ui/Tag";
import { TextLinkButton } from "../ui/buttons/TextLinkButton";
import IdeaActionCardCorner from "./ActionCardCorner/IdeaActionCardCorner";
import ImpactActionCardCorner from "./ActionCardCorner/ImpactActionCardCorner";
import { ActionHelpModal } from "./ActionHelpModal";
import {
  getActionDescriptionTranslationKey,
  getActionTitleTranslationKey,
} from "./utils";
import { RegionalNamespaces } from "@pslifestyle/common/src/constants/translations";
import { WholeAction, TagTheme } from "@pslifestyle/common/src/types/planTypes";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  action: WholeAction;
  subHeading?: string;
  children: ReactNode;
  cyData?: string;
  showHelpLinks?: boolean;
  showGranularActionPotential?: boolean;
}

const ActionCard = ({
  action,
  subHeading,
  children,
  cyData,
  showHelpLinks,
  showGranularActionPotential,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("recommendations");
  const country = useSelector(locationSelectors.country);
  const language = useSelector(locationSelectors.language);
  const { languageCode } = parseLanguageCode(language);
  const [showMore, setShowMore] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showFormulaErrorDetails, setShowFormulaErrorDetails] = useState(false);
  const { category, calculatedImpact, percentReduction } = action;

  if (!country || !languageCode) return null;

  const renderActionDescription = () => {
    if (action.richTextDescription) {
      return (
        <RichText
          content={action.richTextDescription}
          localizedContent={action.localizedRichTextDescription}
          languageCode={languageCode}
        />
      );
    }
    return t(getActionDescriptionTranslationKey(action.id, country.code), {
      ns: RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
    })
      .split("\n")
      .filter((str) => str.length > 0)
      .map((str, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={`str${index}`}>{str}</p>
      ));
  };

  const notifyFormulaError = !IS_PROD && action.calculateImpactError;
  const errorSourceLabel =
    action.calculateImpactErrorSource === "displayCondition"
      ? "Display condition"
      : "Impact formula";
  if (notifyFormulaError) {
    console.error("The following action has a problem with its formula:", {
      action,
      source: action.calculateImpactErrorSource,
      errorMessage: action.calculateImpactErrorMessage,
      formula: action.calculateImpactErrorFormula,
    });
  }
  return (
    <>
      <div className="relative">
        <div
          className={`${notifyFormulaError ? "bg-red-40" : "bg-basic-white"} grow rounded-2xl`}
          data-cy={cyData}
        >
          <div className="flex flex-row">
            <div className="grow pt-6 pl-5 pb-1">
              <Tag
                filter={action.category.toLowerCase()}
                key={action.category}
                variant={action.category.toLowerCase() as TagTheme}
              >
                {t(`categories.${action.category.toLowerCase()}`, {
                  ns: "common",
                })}
              </Tag>
              <Heading level={3} type="title-md" className="mt-3">
                {t(getActionTitleTranslationKey(action.id, country.code), {
                  ns: RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
                })}
              </Heading>
              {notifyFormulaError && (
                <p className="text-red-100">Error with impact formula!</p>
              )}
              {subHeading && (
                <Heading level={4} type="title-sm" className="text-grey-60">
                  {subHeading}
                </Heading>
              )}
              <TextLinkButton
                onClick={() => setShowMore(!showMore)}
                icon={{
                  position: "right",
                  size: "medium",
                  type: showMore ? "ChevronUp" : "ChevronDown",
                }}
                buttonClassName="mt-2.5"
              >
                {showMore ? t("hide", { ns: "common" }) : t("seeMore")}
              </TextLinkButton>
            </div>
            <div>
              {Math.round(action.calculatedImpact) > 0 ? (
                <ImpactActionCardCorner
                  category={category}
                  calculatedImpact={calculatedImpact}
                  percentReduction={percentReduction}
                  showGranularActionPotential={showGranularActionPotential}
                />
              ) : (
                <IdeaActionCardCorner category={category} />
              )}
            </div>
          </div>
          <div
            className={`ml-3 overflow-hidden transition-max-height ease-in-out duration-300 ${
              showMore ? "max-h-screen" : "max-h-0"
            }`}
          >
            <div className="flex flex-col gap-4 px-2 mb-2 body-md">
              {renderActionDescription()}
              {(action.help || action.richTextHelp) && (
                <TextLinkButton
                  className="text-cyan-dark items-start"
                  buttonClassName="flex"
                  onClick={() => setShowHelp(true)}
                >
                  {t("needHelpWithThisAction")}
                </TextLinkButton>
              )}
            </div>
          </div>
          <div className="flex flex-col mx-auto w-full mt-2.5">
            {children}
            {notifyFormulaError && (
              <TextLinkButton
                onClick={() =>
                  setShowFormulaErrorDetails(!showFormulaErrorDetails)
                }
                buttonClassName="mt-2 mb-2"
                className="mx-auto"
              >
                {showFormulaErrorDetails
                  ? "Hide formula error details"
                  : "Show formula error details"}
              </TextLinkButton>
            )}
          </div>
        </div>
        {notifyFormulaError && showFormulaErrorDetails && (
          <div className="absolute top-4 right-0 z-10 w-72 translate-x-[calc(100%+0.75rem)] rounded-2xl border border-red-100 bg-basic-white p-4">
            <p className="text-red-100 body-sm font-semibold">
              Formula debug details
            </p>
            <p className="mt-2 body-sm font-semibold">Source</p>
            <p className="body-sm break-words">{errorSourceLabel}</p>
            <p className="mt-2 body-sm font-semibold">Error</p>
            <p className="body-sm break-words">
              {action.calculateImpactErrorMessage ?? "No error message found."}
            </p>
            <p className="mt-2 body-sm font-semibold">Formula</p>
            <p className="body-sm break-all">
              {action.calculateImpactErrorFormula ?? "No formula found."}
            </p>
          </div>
        )}
      </div>
      <ActionHelpModal
        open={showHelp}
        onClose={() => setShowHelp(false)}
        action={action}
        showHelpLinks={showHelpLinks}
        country={country}
      />
    </>
  );
};

export default ActionCard;
